/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild, Input, ElementRef, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { EmGroup } from "../models/em-group";
import { GroupType } from '../models/group-type';
import { InstanceData } from "../models/instance-data";
import { ObjectCategory } from '../models/object-category';
import { ObjectDefinition } from '../models/object-definition';
import { ObjectInstance } from '../models/object-instance';
import { TagInstance } from "../models/tag-instance";

import { GroupService } from '../services/group.service';
import { ObjectService } from '../services/object.service';
import { TagService } from "../services/tag.service";

import { ConfirmDeleteDialogComponent } from './confirm-delete-dialog.component';
import { ManageGroupsDialogComponent } from './manage-groups-dialog.component';
import { ShowGroupDialogComponent } from './show-group-dialog.component';
import { AddToGroupDialogComponent } from './add-to-group-dialog.component';
import { RemoveFromGroupDialogComponent } from './remove-from-group-dialog.component';

import { EntityInstanceDialogComponent } from './infrastructure/dialogs/entity-instance-dialog.component';
import { EntityTypeDialogComponent } from "./infrastructure/dialogs/entity-type-dialog.component";
import { INFRA_DUMMY_FUNCTION, RESULTS_PER_PAGE } from "../app-constants";
import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from 'ngx-spinner';
import { FunctionParameterDataUi } from './infrastructure/dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from './simple-success-error-dialog-box/simple-success-error-dialog-box.component';


/** Filter Option representing the "REMOVE FILTER" Option */
const FilterOption_RemoveFilter = { column: null, label: "REMOVE FILTER" } as FilterOption;

@Component({
  templateUrl: './main-assets.component.html',
  selector: 'app-main-assets-component',
  styleUrls: ['../../assets/css/infrastructure.css']
})
export class MainAssetsComponent implements OnInit {

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  @ViewChild('fileTypeUpload')
  mTypeFileUploadInput: ElementRef;

  @ViewChild('fileInstanceUpload')
  mInstanceFileUploadInput: ElementRef;

    /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  @Input()
  displayHeader = true;


  mAssetTypeUis: AssetObjectUI[] = [];
  mAssetInstanceUis: AssetInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Assets, 2 = Asset Instances

  /* 
 * Filter Variables
 */
  /** Filter Options available to the Asset Types */
  filterOptionsForTypes: FilterOption[] = [];
  /** Selected Filter Option for the Asset Types */
  filterOptionForTypes: FilterOption = FilterOption_RemoveFilter;
  /** Current Value for the Filter Option being used for the Asset Types */
  filterValueForTypes: string = "";
  /** Filter options available to theAsset Instances */
  filterOptionsForInstances: FilterOption[] = [];
  /** Selected Filter Option for the Asset Instances */
  filterOptionForInstances: FilterOption = FilterOption_RemoveFilter;
  /** Current Value for the Filter Option being used for the Asset Instances */
  filterValueForInstances: string = "";

  //Entity Type Dialog Variables
  mEntity: ObjectDefinition;
  mEntityName: string;
  mEntityType = GroupType.OBJECT;
  mEntityTypeName = "Asset";

  //Entity Instance Dialog Variables
  mInstance: ObjectInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: ObjectDefinition;
  mObjectCategory = ObjectCategory.ASSET;
  mInstanceType = GroupType.OBJECTINSTANCE;
  mInstanceTypeName = "Asset";
  mAvailableEntityTypes: ObjectDefinition[];


  mSelectedTags: TagInstance[];
  mAvailableTags: TagInstance[];
  mOriginalTags: TagInstance[];

  /*
   * Paginator Variables for both Types and Instances
   */
  /** Total # of "Asset" Types known to the system. (Used to figure out # of pages) */
  totalNumberOfTypes = 0;
  /** Total # of "Asset" Instances known to the system. (Used to figure out he # of pages) */
  totalNumberOfInstances = 0;
  /** Current Page being displayed for the "Asset" Types */
  currentPageForTypes = 1;
  /** Current Page being displayed for the "Asset" Instances */
  currentPageForInstances = 1;

  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];

  constructor(
    private mRoute: ActivatedRoute,
    private mObjectService: ObjectService,
    private mGroupService: GroupService,
    private mTagService: TagService,
    private spinnerService: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.refreshFilterOptionsForTypes();
    this.refreshFilterOptionsForInstances();
    this.selectedPageSizeValueForTypes = this.resultsPerPageValues[0];
    this.selectedPageSizeValueForInstances = this.resultsPerPageValues[0];

    this.mRoute.data.subscribe(
      (data:
        {
          assets: PagedResults<ObjectDefinition>,
          assetInstances: PagedResults<ObjectInstance>
        }
      ) => {
        this.updatePagedTypes(data.assets);
        this.updatePagedInstances(data.assetInstances);
      });
  }

  onTypePageChange(pageNumber?: number): void {
    // Make sure the page number is valid... If not, ignore the call.
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    // Make sure the page number is changing... If not, ignore the call
    if (this.currentPageForTypes == pageNumber) {
      return;
    }

    // Update the current page number and refresh the data
    this.currentPageForTypes = pageNumber;
    this.refreshTypes();
  }

  private async refreshTypes(): Promise<void> {
    // Show the spinner (see this being done in the Tags page)
    this.spinnerService.show();

    // Get the results for the current page / filtering
    let pagedData: PagedResults<ObjectDefinition>;
    try {
      pagedData = await this.mObjectService.getAllObjectsForCategoryPaging(ObjectCategory.ASSET, (this.currentPageForTypes - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.filterValueForTypes);
    } catch (error) {
      // Hide the Spinner / Handle the Error
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    // Update the Page Data and hide the Spinner
    this.updatePagedTypes(pagedData);
    this.spinnerService.hide();
  }

  private updatePagedTypes(pagedData: PagedResults<ObjectDefinition>): void {
    // Create the UI Objects for the Types, update the [] being displayed by the UI
    const uiObjects: AssetObjectUI[] = [];
    if (pagedData.data != null) {
      pagedData.data.forEach(asset => {
        const ui = new AssetObjectUI();
        ui.mAssetType = asset;
        ui.mSelected = false;
        uiObjects.push(ui);
      });
    }
    this.mAssetTypeUis = uiObjects;

    // Update the Pagination variables 
    this.totalNumberOfTypes = pagedData.size;
  }

  onInstancePageChange(pageNumber?: number): void {
    // Make sure the page number is valid... If not, ignore the call.
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    // Make sure the page number is changing... If not, ignore the call
    if (this.currentPageForInstances == pageNumber) {
      return;
    }

    // Update the current page number and refresh the data
    this.currentPageForInstances = pageNumber;
    this.refreshInstances();
  }

  private async refreshInstances(): Promise<void> {
    // Show the spinner (see this being done in the Tags page)
    this.spinnerService.show();

    // Get the results for the current page / filtering
    let pagedData: PagedResults<ObjectInstance>;
    try {
      pagedData = await this.mObjectService.getAllObjectInstancesForCategoryPaging(ObjectCategory.ASSET, (this.currentPageForInstances - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.filterValueForInstances);
    } catch (error) {
      // Hide the Spinner / Handle the Error
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    // Update the Page Data and hide the Spinner
    this.updatePagedInstances(pagedData);
    this.spinnerService.hide();
  }


  private updatePagedInstances(pagedData: PagedResults<ObjectInstance>): void {
    // Create the InstanceUI Objects for the Instances, update the [] being displayed by the UI
    const uiObjects: AssetInstanceUI[] = [];
    if (pagedData.data != null) {
      pagedData.data.forEach(assetInstance => {
        const ui = new AssetInstanceUI();
        ui.mAssetInstance = assetInstance;
        ui.mSelected = false;
        uiObjects.push(ui);
      });
    }
    this.mAssetInstanceUis = uiObjects;

    // Update the Pagination variables 
    this.totalNumberOfInstances = pagedData.size;
  }

  manageResultPerPageClickedForTypes(value: number): void {
    this.selectedPageSizeValueForTypes = value;
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  manageResultPerPageClickedForInstances(value: number): void {
    this.selectedPageSizeValueForInstances = value;
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }


  private refreshFilterOptionsForTypes(): void {
    // Remove any existing Filters...
    while (this.filterOptionsForTypes.length > 0) {
      this.filterOptionsForTypes.pop();
    }

    // Add the "REMOVE FILTER" Option
    this.filterOptionsForTypes.push(FilterOption_RemoveFilter);
    // Add the "Name" Option
    this.filterOptionsForTypes.push({ column: "name", label: "Name" } as FilterOption);
  }

  private refreshFilterOptionsForInstances(): void {
    // Remove any existing Filters...
    while (this.filterOptionsForInstances.length > 0) {
      this.filterOptionsForInstances.pop();
    }

    // Add the "REMOVE FILTER" Option
    this.filterOptionsForInstances.push(FilterOption_RemoveFilter);
    // Add the "Name" Option
    this.filterOptionsForInstances.push({ column: "name", label: "Name" } as FilterOption);
  }

 
  isSingleAssetTypeSelected(): boolean {
    if (this.mAssetTypeUis != null && this.mAssetTypeUis.length > 0) {
      let oneSelected = false;
      for (const assetObject of this.mAssetTypeUis) {
        if (assetObject.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }


  isAnyAssetTypesSelected(): boolean {
    return this.mAssetTypeUis.some(assetObject => assetObject.mSelected);
  }


  isAllAssetTypesSelected(): boolean {
    return this.mAssetTypeUis !== null && this.mAssetTypeUis.every(assetObject => assetObject.mSelected);
  }


  setAllAssetTypesSelected(event: any): void {
    this.mAssetTypeUis.forEach(assetObject => assetObject.mSelected = event.target.checked);
  }


  getSelectedAssetTypes(): ObjectDefinition[] {
    const assets: ObjectDefinition[] = [];
    this.mAssetTypeUis.filter(ui => ui.mSelected).forEach(ui => assets.push(ui.mAssetType));
    return assets;
  }


  getSelectedAssetTypeNames(): string[] {
    const names: string[] = [];
    this.mAssetTypeUis.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mAssetType.name));
    return names;
  }


  getSelectedAssetTypeIds(): number[] {
    const ids: number[] = [];
    this.mAssetTypeUis.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mAssetType.id));
    return ids;
  }


  manageAssetTypeGroups(): void {
    this.mManageGroupsDialog.show(GroupType.OBJECT);
  }


  addAssetTypesToGroupClicked(): void {
    if (this.isAnyAssetTypesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.OBJECT, this.getSelectedAssetTypeNames());
    }
  }
  removeAssetTypesFromGroupClicked(): void {
    if (this.isAnyAssetTypesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const assetTypeNames: string[] = [];
      this.getSelectedAssetTypes().forEach(assetType => {
        assetTypeNames.push(assetType.name);
        if (assetType.groups != null) {
          assetType.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, assetTypeNames);
    }
  }


  isSingleAssetInstanceSelected(): boolean {
    if (this.mAssetInstanceUis != null && this.mAssetInstanceUis.length > 0) {
      let oneSelected = false;
      for (const assetInstances of this.mAssetInstanceUis) {
        if (assetInstances.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }


  isAnyAssetInstancesSelected(): boolean {
    return this.mAssetInstanceUis.some(assetInstance => assetInstance.mSelected);
  }


  isAllAssetInstancesSelected(): boolean {
    return this.mAssetInstanceUis !== null && this.mAssetInstanceUis.every(assetInstance => assetInstance.mSelected);
  }


  setAllAssetInstancesSelected(event: any): void {
    this.mAssetInstanceUis.forEach(assetInstance => assetInstance.mSelected = event.target.checked);
  }


  getSelectedAssetInstances(): ObjectInstance[] {
    const assetInstances: ObjectInstance[] = [];
    this.mAssetInstanceUis.filter(ui => ui.mSelected).forEach(ui => assetInstances.push(ui.mAssetInstance));
    return assetInstances;
  }


  getSelectedAssetInstanceNames(): string[] {
    const names: string[] = [];
    this.mAssetInstanceUis.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mAssetInstance.name));
    return names;
  }


  getSelectedAssetInstanceIds(): number[] {
    const ids: number[] = [];
    this.mAssetInstanceUis.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mAssetInstance.id));
    return ids;
  }


  manageAssetInstanceGroups(): void {
    this.mManageGroupsDialog.show(GroupType.OBJECTINSTANCE);
  }


  addAssetInstancesToGroupClicked(): void {
    if (this.isAnyAssetInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.OBJECTINSTANCE, this.getSelectedAssetInstanceNames());
    }
  }


  removeAssetInstancesFromGroupClicked(): void {
    if (this.isAnyAssetInstancesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const assetInstanceNames: string[] = [];
      this.getSelectedAssetInstances().forEach(assetInstance => {
        assetInstanceNames.push(assetInstance.name);
        if (assetInstance.groups != null) {
          assetInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, assetInstanceNames);
    }
  }


  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedAssetTypeIds())
          .then(
            success => {
              this.refreshTypes();
            },
            error => {
              const response: Response = error;
              alert(response.text());
            }
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedAssetInstanceIds())
          .then(
            success => {
              this.refreshInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }


  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedAssetTypeIds())
          .then(
            success => {
              this.refreshTypes();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedAssetInstanceIds())
          .then(
            success => {
              this.refreshInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }


  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .getAllObjectsInGroup(group.id)
          .then(
            result => {
              const itemNames: string[] = [];
              result.forEach(groupMember => itemNames.push(groupMember.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .getAllObjectInstancesInGroup(group.id)
          .then(
            result => {
              const itemNames: string[] = [];
              result.forEach(groupMember => itemNames.push(groupMember.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }


  addAssetClicked(): void {
    const newEntity = new ObjectDefinition();
    newEntity.category = ObjectCategory.ASSET;

    this.mEntity = newEntity;
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAssetTypeUis.map(ui => ui.mAssetType);
    this.mEntityTypeDialogComponent.entityParent = null;
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedAssetClicked(): void {
    if (this.isSingleAssetTypeSelected()) {
      this.editAsset(this.getSelectedAssetTypes()[0]);
    }
  }


  duplicateSelectedAssetClicked(): void {
    if (this.isSingleAssetTypeSelected()) {
      this
        .mObjectService
        .getObjectForId(this.getSelectedAssetTypes()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }
            if (response.functions != null) {
              response.functions.forEach(f => f.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }


  deleteSelectedAssetsClicked(): void {
    if (this.isAnyAssetTypesSelected()) {
      this.mDeleteFlag = 1; //1 = Assets
      this.mConfirmDeleteDialog.show("asset type", this.getSelectedAssetTypeNames());
    }
  }


  async editAsset(asset: ObjectDefinition): Promise<void> {
    //Get the latest values from the server
    let assetType: ObjectDefinition = null;
    try {
      assetType = await this.mObjectService.getObjectForId(asset.id);
    } catch (error) {
      this.handleError(error);
      return;
    }
    //Display the Dialog to edit the Asset Type
    if (assetType != null) {
      //Update the reference to the "Parent" Type to the one that will be in the list of options if the Asset Type has one
      if (assetType.parent != null) {
        const parentUi = this.mAssetTypeUis.find(ui => ui.mAssetType.id == assetType.parent.id);
        if (parentUi == null) {
          assetType.parent = null;
        } else {
          assetType.parent = parentUi.mAssetType;
          console.log("Setting Parent Type to '" + assetType.parent.id + "/" + assetType.parent.name + "'...");
        }
      }
      this.mEntity = assetType;
      this.mEntityName = assetType.name;
      if (assetType.parent != null) {
        this.mEntityTypeDialogComponent.entityParent = assetType.parent;
      } else {
        this.mEntityTypeDialogComponent.entityParent = null;
      }
      this.mEntityTypeDialogComponent.mParents = this.mAssetTypeUis.map(ui => ui.mAssetType);
      console.log("ShowDialog: Entity Type Asset");
      this.mEntityTypeDialogComponent.showDialog();
    }
  }


  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Asset... Create it
      this
        .mObjectService
        .createObject(this.mEntity)
        .then(
          response => {
            this.refreshTypes();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Asset... Update it
      this
        .mObjectService
        .updateObject(this.mEntity)
        .then(
          response => {
            this.refreshTypes();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  addAssetInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new ObjectInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataRefreshTagsAndShowDialog();
  }

  editSelectedAssetInstanceClicked(): void {
    if (this.isSingleAssetInstanceSelected()) {
      this.editAssetInstance(this.getSelectedAssetInstances()[0]);
    }
  }


  duplicateSelectedAssetInstanceClicked(): void {
    if (this.isSingleAssetInstanceSelected()) {
      this
        .mObjectService
        .getObjectInstanceForId(this.getSelectedAssetInstances()[0].id)
        .then(
          response => {
            //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
            this.mInstance = response;

            //Set the Instance Name value so that it gets properly displayed in the Dialog Header
            this.mInstanceName = response.name;

            //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
            this.mInstance.id = null;
            this.mInstance.uuid = null;
            if (this.mInstance.data != null) {
              this.mInstance.data.forEach(d => d.id = null);
            }

            //Refresh the Available Types, Reload the Instance Data and Show the Dialog to start editing
            this.refreshTypesReloadDataRefreshTagsAndShowDialog();
          },
          error => this.handleError(error)
        );
    }
  }


  deleteSelectedAssetInstancesClicked(): void {
    if (this.isAnyAssetInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Asset Instances
      this.mConfirmDeleteDialog.show("asset", this.getSelectedAssetInstanceNames());
    }
  }


  editAssetInstance(assetInstance: ObjectInstance): void {
    this
      .mObjectService
      .getObjectInstanceForId(assetInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Available Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataRefreshTagsAndShowDialog();
        },
        error => this.handleError(error)
      );
  }


  async refreshTypesReloadDataRefreshTagsAndShowDialog(): Promise<void> {
    //Create a List of the available Assets that the Instance can be a type of from the List already obtained
    this.mAvailableEntityTypes = await this.mObjectService.getAllAssetObjects();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.object == null ? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.object.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.functions = this.mInstance.object.functions;
      this.mInstanceEntityType.properties = this.mInstance.object.properties;
    }

    // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
    this.cdr.detectChanges();

    //Refresh the Instance Data, loading the existing values in for the Instance being edited
    this.mEntityInstanceDialogComponent.refreshData();

    //Set the Tag that is assigned to the Asset
    this.mOriginalTags = [];
    this.mSelectedTags = [];
    this.mAvailableTags = [];
    if (this.mInstance == null || this.mInstance.id == null) {
      this.refreshTagsReloadDataAndShowDialog();
    } else {
      this
        .mObjectService
        .getAssignedTagsForObjectInstance(this.mInstance.id)
        .then(
          assignedTags => {
            //Store the Tag that is assigned to the Asset
            console.log("Got tags for objectInstance" + this.mInstance.id);
            this.mOriginalTags = assignedTags;
            this.mSelectedTags = assignedTags;
            for (const assignedTag of assignedTags) {
              this.mAvailableTags.push(assignedTag);
            }

            this.refreshTagsReloadDataAndShowDialog();
          },
          error => this.handleError(error)
        );
    }
  }


  private refreshTagsReloadDataAndShowDialog(): void {
    this
      .mTagService
      .getAllUnassignedTagInstances()
      .then(
        availableTags => {
          if (!availableTags) {
            availableTags = [];
          }
          // Add the selectedTags to the available tags so we can deselect it if we need to
          if (this.mSelectedTags != null) {
            for (const selectedTag of this.mSelectedTags) {
              availableTags.push(selectedTag);
            }
          }
          this.mAvailableTags = availableTags;

          // Need to add the tag(s) the asset instance has (if any) as well
          const numberAvailableTags = this.mAvailableTags.length;
          console.log("Available Tags: " + numberAvailableTags + " emAssets");

          const numberSelectedTags = (this.mSelectedTags) ? this.mSelectedTags.length : 0;
          console.log("Tags assigned: " + numberSelectedTags + " emAssets");

          //Refresh the Instance Data, loading the existing values in for the Instance being edited
          // this.reloadInstanceData(true);
          this.mEntityInstanceDialogComponent.refreshData();

          //Show the Entity Instance Dialog
          this.mEntityInstanceDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }


  async createOrUpdateEntityInstance(): Promise<void> {
    //Set the values to the Asset Instance to be created
    this.mInstance.object = this.mInstanceEntityType;

    try {
      let asset = null;
      if (this.mInstance.id == null) {
        //ID is null, create a new People Object Instance
        asset = await this.mObjectService.createObjectInstance(this.mInstance);
      } else {
        // ID exists, update the People Object Instance
        asset = await this.mObjectService.updateObjectInstance(this.mInstance);
      }

      // Asset created or updated successfully!  If there is a tag/tags selected, assign it now...
      try {
        // Figure out if the tags originally assigned are different than the tags selected
        let tagsDifferent = false;

        if (this.mSelectedTags.length != this.mOriginalTags.length) {
          tagsDifferent = true;
        }

        if (this.mSelectedTags.length > 0 && this.mOriginalTags.length > 0) {
          for (const selectedTag of this.mSelectedTags) {
            if (!this.mOriginalTags.find(tag => selectedTag.id == tag.id)) {
              tagsDifferent = true;
              break;
            }
          }
        }
        if (tagsDifferent) {
          if (this.mInstance.id != null && this.mOriginalTags.length > 0) {
            // Unassign tags if this is an updated asset and the asset had tags (and they're different)
            await this.mObjectService.unassignAllTagsFromObjectInstance(asset.id);
          }

          // assign the new selected tags
          await this.mObjectService.setAssignedTagsForObjectInstance(asset.id, this.mSelectedTags);
        }

      } catch (error) {
        alert("People created but problems assigning Tag: " + error.text());
      }
    } catch (error) {
      this.handleError(error);
    } finally {
      this.refreshInstances();
      this.mEntityInstanceDialogComponent.hideDialog();
    }
  }


  selectedEntityTypeChanged(entityType: ObjectDefinition): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mObjectService
        .getObjectForId(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.functions = foundEntityType.functions;
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }


  selectedTagChanged(tagInstance: TagInstance[]): void {
    this.mSelectedTags = tagInstance;
  }


  refreshInstanceData(): void {
    this
      .mObjectService
      .getObjectInstanceForId(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }


  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Assets
      this
        .mObjectService
        .deleteObjects(this.getSelectedAssetTypeIds())
        .then(
          response => {
            this.refreshTypes();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) { //2 = Asset Instances
      this
        .mObjectService
        .deleteObjectInstances(this.getSelectedAssetInstanceIds())
        .then(
          success => {
            this.refreshInstances();
          },
          error => this.handleError(error)
        );
    }
  }


  handleError(error: any): void { 
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      // Show the error in the SimpleSuccessErrorDialogComponent
          this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }

    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }


  // Returns a number as an array so that looping can be done in an ngFor in Angular
  numberAsArray(value: number): number[] {
    return Array(value).fill(0).map((x, i) => i);
  }


  uploadTypeFileClicked(): void {
    this.mTypeFileUploadInput.nativeElement.click();
  }


  onTypeFileUploadChange(event: any): void {
    if (event.target.files.length === 1) {
      this.uploadTypes(event.target.files[0]);
    }
  }


  uploadTypes(file: any): void {
    const formData = new FormData();
    formData.append("file", file);

    this.mObjectService.uploadTypes(formData).then(
      response => {
        console.log('Success');
      },
      error => {
        console.error(error);
      }
    );
  }


  downloadTypes(): void {
    this.mObjectService.downloadTypes().then(
      response => {
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        document.body.appendChild(a);
        //           a.style = 'diplay: none';
        a.href = url;
        a.download = 'assettypes.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error => {
        console.error(error);
      }
    );
  }


  uploadInstanceFileClicked(): void {
    this.mInstanceFileUploadInput.nativeElement.click();
  }


  onInstanceFileUploadChange(event: any): void {
    if (event.target.files.length === 1) {
      this.uploadInstances(event.target.files[0]);
    }
  }
  private async uploadInstances(file: any): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.mObjectService.uploadInstances(formData);
      this.refreshInstances();
      console.log("Successfully uploaded instances!");
    } catch (error) {
      this.handleError(error);
    }
  }


  downloadAssets(): void {
    const selectedTypes = this.mAssetTypeUis.filter(type => type.mSelected);
    if (selectedTypes.length == 1) {
      this.downloadAssetsForTypeId(selectedTypes[0].mAssetType.id);
    }
  }
  private async downloadAssetsForTypeId(typeId: number): Promise<void> {
    try {
      //Call the Server to download the instances, obtain the response...
      const response = await this.mObjectService.downloadInstances(typeId);

      //Handle the Response by making it available to download...
      const blob = new Blob([response], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = 'asset-instances.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      this.handleError(error);
    }
    return;
  }


  /**-------------------------------- FILTER OPTIONS FOR ASSET & INSTANCE ------------------------------------------------------ */


  onFilterOptionForTypesClicked(filterOption: FilterOption): void {
    if (this.filterOptionForTypes == filterOption) {
      return;
    }

    this.filterOptionForTypes = filterOption;
    this.filterValueForTypes = "";
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  onFilterValueForTypesChanged(): void {
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  onFilterOptionForInstancesClicked(filterOption: FilterOption): void {
    if (this.filterOptionForInstances == filterOption) {
      return;
    }

    this.filterOptionForInstances = filterOption;
    this.filterValueForInstances = "";
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }

  onFilterValueForInstancesChanged(): void {
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }


/*  
*  Handling the Right click event only on Asset Instance Name Column
*/
  fetchFunctionList(event: MouseEvent, assetInstance: ObjectInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = []

    // Send the request to get the Reader Instance details using the ID
    this
      .mObjectService
      .getObjectInstanceForId(assetInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.data && Array.isArray(response.object.functions) && (response.object.functions).length > 0) {
              this.functionList = response.object.functions;

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error);
          }
        },
        error => this.handleError(error)
      );

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }
}

class AssetObjectUI {
  mSelected = false;
  mAssetType: ObjectDefinition;
}

class AssetInstanceUI {
  mSelected = false;
  mAssetInstance: ObjectInstance;
}

interface FilterOption {
  column: string;
  label: string;
}